export const UTP_FORMAT = "uptime-datacenter-plan" as const;
export const UTP_VERSION = 1 as const;
export const CATALOG_VERSION = 2 as const;
export const LOCAL_STORAGE_KEY = "uptime-datacenter-planner:workspace:v1";

export type MapId = "garage" | "basement" | "small-colo" | "small-campus";
export type ScenarioId = "sandbox" | "fj1_garage_to_glory" | "scenario_arm_race" | "scenario_garage_to_glory_hard";
export type RackType = "full-42u" | "mid-24u" | "wall-4u";

export type ComponentAvailability = Readonly<Partial<Record<ScenarioId, readonly MapId[]>>>;

export interface ComponentPerformance {
    readonly powerWatts: number | null;
    readonly vCpus?: number;
    readonly memoryGb?: number;
    readonly storageGb?: number;
    readonly storageIops?: number;
    readonly gpuCount?: number;
    readonly gpuMemoryGb?: number;
    readonly gpuSlices?: number;
    readonly forwardingGbps?: number;
    readonly inspectionGbps?: number;
}

export interface ComponentDefinition {
    readonly id: string;
    readonly name: string;
    readonly shortHandle: string;
    readonly category: string;
    readonly sizeU: number;
    readonly color: string;
    readonly imagePath?: string;
    readonly wikiUrl?: string;
    readonly userPlaceable?: boolean;
    readonly availability: ComponentAvailability;
    readonly performance: ComponentPerformance;
}

export interface FixedComponentDefinition {
    readonly id: string;
    readonly componentId: string;
    readonly startU: number;
    readonly localName?: string;
    readonly canRename?: boolean;
    readonly example?: boolean;
}

export interface RackDefinition {
    readonly id: string;
    readonly name: string;
    readonly availabilityZoneId?: string;
    readonly type: RackType;
    readonly sizeU: 4 | 24 | 42;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly depth: number;
    readonly rotation: number;
    readonly gameSlot?: number;
    readonly fixedComponents?: readonly FixedComponentDefinition[];
}

export interface RoomDefinition {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly label: string;
}

export interface MapZoneDefinition {
    readonly id: string;
    readonly name: string;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly color: string;
}

export type MapBorderStyle = "dashed" | "dotted" | "solid";
export type MapLabelWrapMode = "none" | "word";

export interface MapAreaDefinition extends MapZoneDefinition {
    readonly borderOpacity: number;
    readonly borderStyle: MapBorderStyle;
    readonly borderWidth: number;
    readonly fillOpacity: number;
    readonly fontSize: number;
    readonly labelPosition: "center" | "top-left";
    readonly lineHeight: number;
    readonly maxLines: number;
    readonly maxWidth: number;
    readonly wrapMode: MapLabelWrapMode;
}

export interface MapWallDefinition {
    readonly id: string;
    readonly kind: "exterior" | "interior";
    readonly x1: number;
    readonly x2: number;
    readonly y1: number;
    readonly y2: number;
}

export interface MapLineDefinition {
    readonly id: string;
    readonly color: string;
    readonly style: MapBorderStyle;
    readonly thickness: number;
    readonly x1: number;
    readonly x2: number;
    readonly y1: number;
    readonly y2: number;
}

export interface MapBadgeDefinition {
    readonly id: string;
    readonly name: string;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly borderStyle: MapBorderStyle;
    readonly borderThickness: number;
    readonly color: string;
    readonly cornerRadius: number;
    readonly fillOpacity: number;
    readonly fontSize: number;
    readonly textColor: string;
}

export interface MapLabelDefinition {
    readonly id: string;
    readonly name: string;
    readonly x: number;
    readonly y: number;
    readonly alignment: "center" | "left" | "right";
    readonly color: string;
    readonly fontSize: number;
    readonly lineHeight: number;
    readonly maxLines: number;
    readonly maxWidth: number;
    readonly wrapMode: MapLabelWrapMode;
}

export interface MapDefinition {
    readonly id: MapId;
    readonly name: string;
    readonly width: number;
    readonly height: number;
    readonly gridSize: number;
    readonly room: RoomDefinition;
    readonly racks: readonly RackDefinition[];
    readonly zones: readonly MapZoneDefinition[];
    readonly areas: readonly MapAreaDefinition[];
    readonly walls: readonly MapWallDefinition[];
    readonly lines: readonly MapLineDefinition[];
    readonly badges: readonly MapBadgeDefinition[];
    readonly labels: readonly MapLabelDefinition[];
}

export interface Placement {
    readonly id: string;
    readonly componentId: string;
    readonly rackId: string;
    readonly startU: number;
    readonly localName?: string;
}

export interface RackSettings {
    readonly rackId: string;
    readonly name?: string;
    readonly showNameAsComponentPrefix: boolean;
    readonly sizeU?: 24 | 42;
    readonly sizeSource?: "api" | "savegame";
}

export interface ProjectMetadata {
    readonly id: string;
    readonly name: string;
    readonly mapId: MapId;
    readonly scenarioId: ScenarioId;
    readonly createdAt: string;
    readonly updatedAt: string;
}

export interface Plan {
    readonly project: ProjectMetadata;
    readonly placements: readonly Placement[];
    readonly rackSettings: readonly RackSettings[];
}

export interface UtpDocument {
    readonly format: typeof UTP_FORMAT;
    readonly version: typeof UTP_VERSION;
    readonly catalogVersion: number;
    readonly project: ProjectMetadata;
    readonly placements: readonly Placement[];
    readonly rackSettings: readonly RackSettings[];
}

export type AppView = "project" | "floorplan" | "rack" | "catalog";

export interface WorkspaceSnapshot {
    readonly plan: Plan | null;
    readonly selectedRackId: string | null;
    readonly view: AppView;
}

export interface ValidationResult {
    readonly valid: boolean;
    readonly reason?: string;
}

export interface ImportResult {
    readonly ok: boolean;
    readonly plan?: Plan;
    readonly error?: string;
}
